import {
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  ScheduleOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { L } from '@lib';
import {
  cautionButtonStyle,
  DropOffDateStatus,
  DropOffDateStatusObjectKey,
  LocalizedServiceOrderStatus,
  LocalizedServiceOrderStatusObjectKey,
  OrderStatus,
  ServiceOrderStatusColor,
  ServiceOrderStatusColorObjectKey,
} from '@models';
import { NotificationService } from '@services';
import { formatDateWithOffset, formatLoggedTime, isTimeTbc } from '@utils';
import { Alert, Button, DatePicker, InputNumber, Modal, Select, Tooltip, message } from 'antd';
import { SharedCard } from './SharedCard';
import './JobDetailsCard.css';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useStore } from '@stores';
import { useView } from '@hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTimeRangeSelect } from '../Form/DateTimeRangeSelect';
import { ServiceHistoryModal } from '../Form/ServiceHistoryModal';
import { OrderStatusSelect } from '../Select';
import { MasterBuildTag } from '../Tag/MasterBuildTag';
import { AvailableWorkStationSelect } from '../Select/AvailableWorkStationSelect';
import { TechnicianSelect } from '../Select/TechnicianSelect';

// --- Styles ---

// Field icons are grey, as on the customer card - colour is kept for state, not decoration.
const ICON_GREY = '#8c8c8c';

const iconStyle = (color: string): React.CSSProperties => ({
  fontSize: 13,
  color,
  flexShrink: 0,
});

const valueStyle: React.CSSProperties = { fontSize: 13, color: 'rgba(0, 0, 0, 0.88)' };
const emptyValueStyle: React.CSSProperties = { fontSize: 13, color: 'rgba(0, 0, 0, 0.45)' };

// --- Time options ---

const timeOptions = Array.from({ length: 13 }, (_, hour) =>
  Array.from({ length: 4 }, (_, index) => {
    const h = ((hour + 8) % 24).toString().padStart(2, '0');
    const m = (index * 15).toString().padStart(2, '0');
    return { value: `${h}:${m}`, label: `${h}:${m}` };
  })
).flat();

// --- Sub-components ---

// Field label above its control - small, grey, with the field's icon.
const fieldLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: 'rgba(0, 0, 0, 0.55)',
  marginBottom: 4,
};

const Field = ({
  icon,
  text,
  wide = false,
  children,
}: {
  icon: React.ReactElement<{ style?: React.CSSProperties }>;
  text: string;
  /** Spans both columns (the build tag, the sync warning). */
  wide?: boolean;
  children: React.ReactNode;
}) => (
  <div className={wide ? 'job-card-field-wide' : undefined} style={{ minWidth: 0 }}>
    <div style={fieldLabelStyle}>
      {React.cloneElement(icon, { style: iconStyle(ICON_GREY) })}
      {text}
    </div>
    {children}
  </div>
);

const ReadOnlyValue = ({ value, fallback }: { value?: string | null; fallback: string }) => (
  <span style={value ? valueStyle : emptyValueStyle}>{value || fallback}</span>
);

// Read-only display of the time the job has spent in the InProgress status. Counts up
// live while the job is in progress, otherwise shows the frozen accumulated total.
const LoggedTimeValue = ({
  accumulatedMinutes,
  startedAt,
  running,
}: {
  accumulatedMinutes: number;
  startedAt?: string;
  running: boolean;
}) => {
  const startedMs = running && startedAt ? dayjs(startedAt).valueOf() : null;
  const compute = () =>
    accumulatedMinutes + (startedMs ? Math.max(0, (Date.now() - startedMs) / 60000) : 0);
  const [minutes, setMinutes] = useState(compute);

  useEffect(() => {
    setMinutes(compute());
    if (!startedMs) return;
    const id = setInterval(() => setMinutes(compute()), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedMs, accumulatedMinutes]);

  return <ReadOnlyValue value={formatLoggedTime(minutes)} fallback="—" />;
};

// The build this job is a leg of, for the drawer title / page header - it is one tag that never
// changes while the job is open, so it does not need a field of its own on the card.
export const JobBuildTag = observer(() => {
  const { order } = useStore().orderStore;

  if (!order?.masterBuildId) return null;

  return <MasterBuildTag order={order} />;
});

// The InProgress stopwatch, for the "Job card details" heading. Read-only and deliberately away
// from the work log button: that one is hand-logged, per-technician time, and the two differ.
export const JobLoggedTime = observer(() => {
  const { orderStore } = useStore();
  const { order } = orderStore;

  if (!order) return null;

  return (
    <Tooltip title="Stopwatch: total time the job has spent in the In progress status. Technicians' own hours are in the work log.">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
        <FieldTimeOutlined style={iconStyle('#0a7d28')} />
        <span style={{ color: 'rgba(0, 0, 0, 0.65)' }}>Time in progress</span>
        <strong>
          <LoggedTimeValue
            accumulatedMinutes={order.inProgressMinutes ?? 0}
            startedAt={order.inProgressStartedAt}
            running={order.status === OrderStatus.InProgress}
          />
        </strong>
      </span>
    </Tooltip>
  );
});

// --- Types ---
export const JobDetailsCard = observer(() => {
  const { orderStore } = useStore();
  const { isMobile } = useView();
  const { order } = orderStore;
  const [openMileageModal, setOpenMileageModal] = useState(false);
  const [notifying, setNotifying] = useState(false);

  if (!order) return null;

  const isLocked = order.status === OrderStatus.Paid || order.status === OrderStatus.Ready;
  const canEditDropOff = !!DropOffDateStatus[order.status as DropOffDateStatusObjectKey];

  const updateField = useCallback(
    (field: string, value: any) => {
      orderStore.updateOrderField({ [field]: value });
    },
    [orderStore]
  );

  // --- Drop-off helpers ---

  const dropOffDayjs = useMemo(
    () => (order.dropOffDate ? dayjs(order.dropOffDate) : dayjs()),
    [order.dropOffDate]
  );

  const handleDropOffDateChange = useCallback(
    (date: dayjs.Dayjs | null) => {
      if (!date) return;
      const h = dropOffDayjs.hour();
      const m = dropOffDayjs.minute();
      const updated = formatDateWithOffset(dayjs(date).hour(h).minute(m).second(0));
      updateField('dropOffDate', updated);
    },
    [dropOffDayjs, updateField]
  );

  const handleDropOffTimeChange = useCallback(
    (value: string) => {
      const [h, m] = value.split(':');
      const updated = formatDateWithOffset(
        dayjs(order.dropOffDate).hour(Number(h)).minute(Number(m)).second(0)
      );
      updateField('dropOffDate', updated);
    },
    [order.dropOffDate, updateField]
  );

  // --- Drop-off / job-date consistency warning ---

  // Drop-off is expected to fall on the same day as the job. When the job date is moved
  // (forward or backward), the drop-off can land on a different day and needs re-syncing.
  // The check compares the drop-off against the job date only (not "now"). We only warn
  // (nothing is blocked or required) and offer a one-click sync.
  const dropOffOutOfSync = useMemo(() => {
    if (!order.startAt || !order.dropOffDate) return false;
    return !dayjs(order.dropOffDate).isSame(dayjs(order.startAt), 'day');
  }, [order.startAt, order.dropOffDate]);

  // Aligns the drop-off with the job date, keeping its time-of-day but never letting it
  // land after the job start.
  const handleSyncDropOffToJob = useCallback(() => {
    if (!order.startAt) return;
    const jobStart = dayjs(order.startAt);
    const current = order.dropOffDate ? dayjs(order.dropOffDate) : jobStart;
    let synced = jobStart.hour(current.hour()).minute(current.minute()).second(0);
    // A TBC job starts "sometime that day": the drop-off keeps its time rather than being
    // pulled to midnight.
    if (!isTimeTbc(order.startAt) && synced.isAfter(jobStart)) synced = jobStart;
    updateField('dropOffDate', formatDateWithOffset(synced));
  }, [order.startAt, order.dropOffDate, updateField]);

  // --- Handlers ---

  const handleStatusChange = useCallback(
    (status: string) => {
      if (status === OrderStatus.Completed) {
        setOpenMileageModal(true);
      } else if (status === 'Canceled') {
        // Three-button modal: [Go back] cancels the action; [No] cancels the order without restoring inventory; [Yes] cancels and returns parts to stock.
        const modal = Modal.confirm({
          title: 'Cancel this order — return parts to stock?',
          content:
            'The order will be cancelled. Should any inventory parts used on this job be returned to stock?',
          icon: null,
          width: 520,
          okText: 'Yes',
          okButtonProps: { type: 'primary' },
          cancelText: 'No',
          onOk: () => {
            updateField('status', status);
            updateField('restoreInventory', true);
          },
          onCancel: () => {
            updateField('status', status);
            updateField('restoreInventory', false);
          },
          footer: (_, { OkBtn, CancelBtn }) => (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => modal.destroy()}>Go back</Button>
              <CancelBtn />
              <OkBtn />
            </div>
          ),
        });
      } else {
        updateField('status', status);
      }
    },
    [updateField]
  );

  const handleNotifyCustomer = useCallback(async () => {
    if (!order.customer?.userId) {
      message.warning('Customer is not an app user.');
      return;
    }
    setNotifying(true);
    try {
      await NotificationService.create({
        isInitial: false,
        sendPush: true,
        orderId: order.id,
        dropOffDate: String(order.dropOffDate || ''),
      });
      message.success('Customer notified');
    } catch {
      message.error('Failed to notify customer');
    } finally {
      setNotifying(false);
    }
  }, [order.id, order.customer?.userId, order.dropOffDate]);

  // Telling the customer is an action on the drop-off, so it lives in that field rather than as a
  // bar across the card. It sends whatever is on the order, so an unsaved edit must save first -
  // otherwise the customer is told a date the job does not have.
  const dropOffSaved =
    !order.dropOffDate && !orderStore.savedDropOffDate
      ? true
      : !!order.dropOffDate &&
        !!orderStore.savedDropOffDate &&
        dayjs(order.dropOffDate).isSame(dayjs(orderStore.savedDropOffDate));

  const canNotify = !!order.customer?.userId;
  const notifyEnabled = canNotify && dropOffSaved;

  // Always rendered, so the row keeps the same three columns as Job date above it; a customer who
  // is not on the app just sees it disabled, and the hint says why.
  const notifyButton = (
    <Button
      icon={<BellOutlined />}
      disabled={!notifyEnabled}
      loading={notifying}
      onClick={handleNotifyCustomer}
      style={{
        flex: 1,
        minWidth: 0,
        height: 32,
        ...(notifyEnabled ? cautionButtonStyle : {}),
      }}
    >
      Notify customer
    </Button>
  );

  // Says what the button will do, in the space the field's own label leaves - no tooltip to hunt for.
  const notifyHint = (
    <div
      style={{
        fontSize: 11,
        marginTop: 4,
        textAlign: 'right',
        color: canNotify && !dropOffSaved ? '#d4380d' : 'rgba(0, 0, 0, 0.45)',
      }}
    >
      {!canNotify
        ? 'Only customers on the app can be notified of a drop-off change'
        : dropOffSaved
          ? 'Drop off date push notification will be sent to the customer straight away'
          : 'Save the job first - the customer is told the saved drop-off'}
    </div>
  );

  // --- Render ---

  return (
    <SharedCard style={{ height: '100%' }}>
      {/* Two fields per row, label above the control, so the card is three rows tall instead of
          six. The pairs are what gets set together: state and reading, who and where, when. */}
      <div className={`job-card-fields${isMobile ? ' job-card-fields-mobile' : ''}`}>
        <Field icon={<CheckCircleOutlined />} text="Status">
          {isLocked ? (
            <span
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                background:
                  ServiceOrderStatusColor[order.status as ServiceOrderStatusColorObjectKey] ||
                  '#f0f0f0',
                color: 'rgba(0, 0, 0, 0.75)',
              }}
            >
              {LocalizedServiceOrderStatus[order.status as LocalizedServiceOrderStatusObjectKey] ||
                order.status}
            </span>
          ) : (
            <OrderStatusSelect
              hideFirstOption
              hideApproval
              hidePaid={order.status !== OrderStatus.Paid}
              hideReady={order.status !== OrderStatus.Ready}
              style={{ width: '100%' }}
              status={order.status}
              onChange={handleStatusChange}
            />
          )}
        </Field>

        <Field icon={<DashboardOutlined />} text="Mileage">
          {!isLocked ? (
            <InputNumber
              placeholder="Enter mileage"
              style={{ width: '100%' }}
              controls={false}
              value={order.mileage}
              min={0}
              max={1000000}
              formatter={(val) => (val ? `${val}`.replace(/\./g, '') : '')}
              parser={(val) => (val ? Number(val.replace(/\./g, '')) : '') as any}
              onChange={(val) => updateField('mileage', val ?? undefined)}
            />
          ) : (
            <ReadOnlyValue
              value={order.mileage ? `${order.mileage.toLocaleString()} mls` : undefined}
              fallback="—"
            />
          )}
        </Field>

        <Field icon={<UserOutlined />} text="Technician">
          {!isLocked ? (
            <TechnicianSelect
              style={{ width: '100%' }}
              placeholder="Select technician"
              value={order.userId ? Number(order.userId) : undefined}
              disabled={isLocked}
              defaultValue={
                order.user
                  ? [{ label: order.user.fullname, value: Number(order.userId) }]
                  : undefined
              }
              onClear={() => updateField('userId', undefined)}
              onSelect={(id) => updateField('userId', id)}
              onDeselect={() => updateField('userId', undefined)}
            />
          ) : (
            <ReadOnlyValue value={order.user?.fullname} fallback={L('Not Assigned')} />
          )}
        </Field>

        <Field icon={<ToolOutlined />} text="Workstation">
          {!isLocked ? (
            <AvailableWorkStationSelect
              orderId={order.id}
              startAt={order.startAt}
              endAt={order.endAt}
              disabled={isLocked}
              style={{ width: '100%' }}
              value={order.workStationId}
              defaultValue={
                order.workStation
                  ? [{ label: order.workStation.name, value: order.workStationId }]
                  : undefined
              }
              onChange={(val) => updateField('workStationId', val)}
            />
          ) : (
            <ReadOnlyValue value={order.workStation?.name} fallback="Not assigned" />
          )}
        </Field>

        {/* Date plus two time selects never fit half a card, so this one keeps the whole row. */}
        <Field icon={<ScheduleOutlined />} text="Job date" wide>
          {!isLocked ? (
            <DateTimeRangeSelect
              disabled={isLocked}
              dateRange={{ startAt: order.startAt, endAt: order.endAt }}
              setDateRange={(range) =>
                orderStore.updateOrderField({ startAt: range.startAt, endAt: range.endAt })
              }
            />
          ) : (
            <ReadOnlyValue
              value={
                !order.startAt
                  ? undefined
                  : isTimeTbc(order.startAt)
                    ? `${dayjs(order.startAt).format('ddd, MMM DD')} · ${L('TBC')}`
                    : `${dayjs(order.startAt).format('ddd, MMM DD')} ${dayjs(order.startAt).format('HH:mm')} - ${dayjs(order.endAt).format('HH:mm')}`
              }
              fallback={L('Not Assigned')}
            />
          )}
        </Field>

        {/* Date, time and the one action that follows from them - the whole row, like Job date. */}
        <Field icon={<CalendarOutlined />} text="Drop-off" wide>
          {!isLocked && canEditDropOff ? (
            <>
              <div style={{ display: 'flex', gap: 4, width: '100%' }}>
                <DatePicker
                  inputReadOnly
                  showTime={false}
                  allowClear={false}
                  disabledDate={(current) => current < dayjs().startOf('day')}
                  format="DD/MM"
                  style={{ flex: 1, minWidth: 0, height: 32 }}
                  value={dropOffDayjs}
                  onChange={handleDropOffDateChange}
                />
                <Select
                  style={{ flex: 1, minWidth: 0, height: 32 }}
                  options={timeOptions}
                  value={dropOffDayjs.format('HH:mm')}
                  onChange={handleDropOffTimeChange}
                />
                {notifyButton}
              </div>
              {notifyHint}
            </>
          ) : (
            <ReadOnlyValue
              value={
                order.dropOffDate ? dayjs(order.dropOffDate).format('YYYY-MM-DD, HH:mm') : undefined
              }
              fallback="—"
            />
          )}
        </Field>

        {/* Drop-off / job-date out-of-sync warning (non-blocking) */}
        {!isLocked && dropOffOutOfSync && (
          <div className="job-card-field-wide">
            <Alert
              type="error"
              showIcon
              style={{ fontSize: 12 }}
              title="The drop-off date does not match the job date."
              action={
                canEditDropOff ? (
                  <Button size="small" type="primary" onClick={handleSyncDropOffToJob}>
                    Sync drop-off
                  </Button>
                ) : undefined
              }
            />
          </div>
        )}
      </div>

      {/* Mileage modal */}
      {openMileageModal && (
        <ServiceHistoryModal
          orderId={order.id}
          customerId={order.customerId}
          vin={order.vin}
          mileage={order.mileage}
          open={openMileageModal}
          setOpen={setOpenMileageModal}
          onCancel={() => setOpenMileageModal(false)}
        />
      )}
    </SharedCard>
  );
});
